import React, { forwardRef } from 'react';
import AssetIcon from './AssetIcon';
import './assets.css';

// Semantic component aliases for existing screens. All pictograms come from
// the same original catalogue; no second icon library or redrawn SVG paths.
function catalogueIcon(displayName, name, direction) {
  const Icon = forwardRef(function CatalogueIcon({ className = '', size = 32, ...props }, ref) {
    return <AssetIcon {...props} ref={ref} name={name} size={size}
      className={['wt-app-icon', direction ? `wt-icon--${direction}` : '', className].filter(Boolean).join(' ')} />;
  });
  Icon.displayName = displayName;
  Icon.iconName = name;
  return Icon;
}

export const DealInProgress = catalogueIcon('DealInProgress', 'nav.dealsActive');
export const Pending = catalogueIcon('Pending', 'pending');
export const InProgress = catalogueIcon('InProgress', 'inProgress');
export const Activity = catalogueIcon('Activity', 'inProgress');
export const AlertCircle = catalogueIcon('AlertCircle', 'review');
export const AlertTriangle = catalogueIcon('AlertTriangle', 'review');
export const Award = catalogueIcon('Award', 'completed');
export const BarChart3 = catalogueIcon('BarChart3', 'profit');
export const Bell = catalogueIcon('Bell', 'bell');
export const Briefcase = catalogueIcon('Briefcase', 'folder');
export const Building = catalogueIcon('Building', 'finance');
export const Building2 = catalogueIcon('Building2', 'finance');
export const Calculator = catalogueIcon('Calculator', 'coins');
export const Calendar = catalogueIcon('Calendar', 'calendar');
export const CalendarClock = catalogueIcon('CalendarClock', 'calendar');
export const CalendarDays = catalogueIcon('CalendarDays', 'calendar');
export const Check = catalogueIcon('Check', 'completed');
export const CheckCircle = catalogueIcon('CheckCircle', 'completed');
export const CheckCircle2 = catalogueIcon('CheckCircle2', 'completed');
export const CircleDollarSign = catalogueIcon('CircleDollarSign', 'usdCircle');
export const ClipboardList = catalogueIcon('ClipboardList', 'taskIcon');
export const Clock = catalogueIcon('Clock', 'inProgress');
export const Cog = catalogueIcon('Cog', 'config');
export const CreditCard = catalogueIcon('CreditCard', 'finance');
export const Download = catalogueIcon('Download', 'download');
export const Edit = catalogueIcon('Edit', 'edit');
export const Edit3 = catalogueIcon('Edit3', 'edit');
export const Euro = catalogueIcon('Euro', 'usdCircle');
export const Eye = catalogueIcon('Eye', 'preview');
export const EyeOff = catalogueIcon('EyeOff', 'password');
export const FileArchive = catalogueIcon('FileArchive', 'document');
export const FileImage = catalogueIcon('FileImage', 'document');
export const FilePlus = catalogueIcon('FilePlus', 'document');
export const FileSignature = catalogueIcon('FileSignature', 'documentsSigned');
export const FileText = catalogueIcon('FileText', 'note');
export const Filter = catalogueIcon('Filter', 'filter');
export const FolderOpen = catalogueIcon('FolderOpen', 'document');
export const GripVertical = catalogueIcon('GripVertical', 'menu');
export const Handshake = catalogueIcon('Handshake', 'dealsProposals');
export const Image = catalogueIcon('Image', 'document');
export const Inbox = catalogueIcon('Inbox', 'folder');
export const Lightbulb = catalogueIcon('Lightbulb', 'config');
export const ListFilter = catalogueIcon('ListFilter', 'filter');
export const Loader2 = catalogueIcon('Loader2', 'inProgress');
export const Mail = catalogueIcon('Mail', 'mail');
export const MapPin = catalogueIcon('MapPin', 'finance');
export const MessageSquare = catalogueIcon('MessageSquare', 'notes');
export const MoreHorizontal = catalogueIcon('MoreHorizontal', 'menu');
export const Paperclip = catalogueIcon('Paperclip', 'attached');
export const PauseCircle = catalogueIcon('PauseCircle', 'powerOff');
export const Pencil = catalogueIcon('Pencil', 'edit');
export const Phone = catalogueIcon('Phone', 'phone');
export const PieChart = catalogueIcon('PieChart', 'profit');
export const PlayCircle = catalogueIcon('PlayCircle', 'inProgress');
export const Plus = catalogueIcon('Plus', 'add');
export const Power = catalogueIcon('Power', 'powerOff');
export const Receipt = catalogueIcon('Receipt', 'usdCircle');
export const RefreshCw = catalogueIcon('RefreshCw', 'reset');
export const Repeat2 = catalogueIcon('Repeat2', 'review');
export const Reply = catalogueIcon('Reply', 'mail');
export const Save = catalogueIcon('Save', 'save');
export const ScrollText = catalogueIcon('ScrollText', 'note');
export const Search = catalogueIcon('Search', 'search');
export const Send = catalogueIcon('Send', 'mail');
export const ShieldCheck = catalogueIcon('ShieldCheck', 'ownership');
export const Tag = catalogueIcon('Tag', 'folder');
export const Target = catalogueIcon('Target', 'pending');
export const Trash2 = catalogueIcon('Trash2', 'delete');
export const TrendingDown = catalogueIcon('TrendingDown', 'loss');
export const TrendingUp = catalogueIcon('TrendingUp', 'profit');
export const Trophy = catalogueIcon('Trophy', 'completed');
export const Upload = catalogueIcon('Upload', 'upload');
export const User = catalogueIcon('User', 'user');
export const UserCheck = catalogueIcon('UserCheck', 'user');
export const UserCircle = catalogueIcon('UserCircle', 'user');
export const UserPlus = catalogueIcon('UserPlus', 'clients');
export const UserRound = catalogueIcon('UserRound', 'user');
export const UserRoundCog = catalogueIcon('UserRoundCog', 'team');
export const Users = catalogueIcon('Users', 'clients');
export const Wallet = catalogueIcon('Wallet', 'sackDollar');
export const X = catalogueIcon('X', 'crossCircle');
export const XCircle = catalogueIcon('XCircle', 'crossCircle');
export const Zap = catalogueIcon('Zap', 'config');
export const ArrowLeft = catalogueIcon('ArrowLeft', 'arrowDown', 'left');
export const ArrowRight = catalogueIcon('ArrowRight', 'arrowDown', 'right');
export const ChevronDown = catalogueIcon('ChevronDown', 'arrowDown', 'down');
export const ChevronLeft = catalogueIcon('ChevronLeft', 'arrowDown', 'left');
export const ChevronRight = catalogueIcon('ChevronRight', 'arrowDown', 'right');
export const ChevronUp = catalogueIcon('ChevronUp', 'arrowDown', 'up');

// Native control marks are geometry/text, not a separate pictogram catalogue.
function controlMark(displayName, mark, text) {
  const Mark = forwardRef(function ControlMark({ className = '', size = '1em', style, ...props }, ref) {
    return <span {...props} ref={ref} aria-hidden="true"
      className={`wt-control-mark wt-control-mark--${mark} ${className}`}
      style={{ '--wt-control-size': typeof size === 'number' ? `${size}px` : size, ...style }}>{text}</span>;
  });
  Mark.displayName = displayName;
  return Mark;
}
export const Circle = controlMark('Circle', 'dot');
export const Minus = controlMark('Minus', 'minus');
export const Percent = controlMark('Percent', 'percent', '%');
